import { LegalDocument } from '@models/LegalDocument'
import { legalDocumentSchema } from '@models/schema'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import {
  createLegalDocument as createLegalDocumentAPI,
  deleteLegalDocument as deleteLegalDocumentAPI,
  fetchLegalDocumentBySlug,
  fetchLegalDocuments,
  updateLegalDocumentActiveVersion as updateLegalDocumentActiveVersionAPI,
  updateLegalDocument as updateLegalDocumentAPI,
} from '@services/LegalDocumentService'
import { AppDispatch, RootState } from '@store/store'
import {
  baseReducers,
  getIdOrModelId,
  getSlugOrModelSlug,
  SBAPI,
  SBSelectRaw,
  SBState,
} from '@supplement-bacon/react-toolkit/utils'
import { initialQuery, TableParams } from '@supplement-bacon/sb-table-filters'

const initialState: SBState<LegalDocument> & {
  selectedSlug: string | undefined
} = {
  isLoading: false,
  error: null,
  items: {},
  ids: [],
  selectedId: undefined,
  selectedSlug: undefined,
  query: initialQuery,
}

const slice = createSlice({
  name: 'legalDocument',
  initialState,
  reducers: {
    ...baseReducers,
    setSelectedSlug: (state, action) => ({
      ...state,
      selectedSlug: action.payload,
    }),
  },
})

// Reducer
export default slice.reducer
export const {
  getItemsSuccess: getLegalDocumentsSuccess,
  setQuery: setLegalDocumentQuery,
  reset: resetLegalDocumentState,
  resetQueryAndIds: resetLegalDocumentQueryAndIds,
} = slice.actions

/**
 * Selectors
 */

const selectRawItems: SBSelectRaw<
  RootState,
  { [key: number]: LegalDocument }
> = (state: RootState) => state[slice.name].items
const selectRawIds: SBSelectRaw<RootState, number[]> = (state: RootState) =>
  state[slice.name].ids
const selectRawSelectedId: SBSelectRaw<RootState, number | undefined> = (
  state: RootState
) => state[slice.name].selectedId

export const selectLegalDocuments = () =>
  createSelector(
    [selectRawItems, selectRawIds],
    (items, ids) => ids.map((id) => items[id]).filter((i) => i)
    // Filter allow to return only non-null elements
  )
export const selectSelectedLegalDocument = () =>
  createSelector([selectRawItems, selectRawSelectedId], (items, id) =>
    id !== undefined ? items[id] : undefined
  )
export const selectLegalDocumentById = (id: number) =>
  createSelector([selectRawItems], (items) =>
    items.hasOwnProperty(id) ? items[id] : undefined
  )
export const selectLegalDocumentsByIds = (ids: number[]) =>
  createSelector([selectRawItems], (items) =>
    ids.filter((id) => items.hasOwnProperty(id)).map((id) => items[id])
  )
export const selectLegalDocumentBySlug = (slug: string) =>
  createSelector([selectRawItems], (items) =>
    Object.entries(items)
      .map((i) => i[1])
      .find((legalDocument) => legalDocument.slug === slug)
  )

/**
 * Actions
 */

export const setSelectedLegalDocument =
  (legalDocument: LegalDocument | number) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedId(getIdOrModelId<LegalDocument>(legalDocument))
    )

export const setSelectedLegalDocumentWithSlug =
  (legalDocument: LegalDocument | string) => async (dispatch: AppDispatch) =>
    dispatch(
      slice.actions.setSelectedSlug(
        getSlugOrModelSlug<LegalDocument>(legalDocument)
      )
    )

export const getLegalDocuments = (params: TableParams) =>
  SBAPI.FetchPaginatedDispatched(
    fetchLegalDocuments(params),
    [legalDocumentSchema],
    slice.actions
  )

export const getLegalDocumentWithSlug = (slug: string) =>
  SBAPI.FetchDispatched<LegalDocument>(
    fetchLegalDocumentBySlug(slug),
    legalDocumentSchema,
    slice.actions
  )

export const createLegalDocument = (legalDocument: LegalDocument) =>
  SBAPI.CreateDispatched(
    createLegalDocumentAPI(legalDocument),
    legalDocumentSchema,
    slice.actions
  )

export const updateLegalDocument = (legalDocument: LegalDocument) =>
  SBAPI.UpdateDispatched<LegalDocument>(
    updateLegalDocumentAPI(legalDocument),
    legalDocument,
    slice.actions
  )

export const updateLegalDocumentActiveVersion = (
  legalDocument: LegalDocument,
  newActiveVersionId: number
) =>
  SBAPI.UpdateDispatched<LegalDocument>(
    updateLegalDocumentActiveVersionAPI(legalDocument, newActiveVersionId),
    legalDocument,
    slice.actions
  )

export const deleteLegalDocument = (legalDocument: LegalDocument) =>
  SBAPI.DestroyDispatched<LegalDocument>(
    deleteLegalDocumentAPI(legalDocument),
    legalDocument,
    slice.actions
  )
